import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import HomePage from "@/pages/HomePage";
import OnboardSimple from "@/pages/OnboardSimple";
import OnboardTablet from "@/pages/OnboardTablet";
import SalesKit from "@/pages/SalesKit";
import AdminRepLeaderboard from "@/pages/AdminRepLeaderboard";
import ContractorGrow from "@/pages/ContractorGrow";
import AdminNCGE from "@/pages/AdminNCGE";
import ContractorReferrals from "@/pages/ContractorReferrals";
import AdminReferralPayouts from "@/pages/AdminReferralPayouts";
import ReferralRedirect from "@/pages/ReferralRedirect";
import AdminSubscribers from "@/pages/AdminSubscribers";
import RetentionSavePage from "@/pages/RetentionSavePage";
import AdminRetentionPage from "@/pages/AdminRetentionPage";
import PricingPage from "@/pages/PricingPage";
import WelcomePage from "@/pages/WelcomePage";
import ContactPage from "@/pages/ContactPage";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminInbox from "@/pages/AdminInbox";
import WhyNearNow from "@/pages/WhyNearNow";
import JobsToday from "@/pages/JobsToday";
import TestimonialsPage from "@/pages/TestimonialsPage";
import AdminMarketing from "@/pages/AdminMarketing";
import LeadsPage from "@/pages/LeadsPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import AdminUsersPage from "@/pages/AdminUsersPage";
import MyRepStatsPage from "@/pages/MyRepStatsPage";
import ChangePasswordPage from "@/pages/ChangePasswordPage";
import AdminDiscountCodesPage from "@/pages/AdminDiscountCodesPage";
import BusinessHealthAssessmentPage from "@/pages/BusinessHealthAssessmentPage";
import AssessmentReportPage from "@/pages/AssessmentReportPage";
import AdminAuthCallbackPage from "@/pages/AdminAuthCallbackPage";
import AdminForgotPasswordPage from "@/pages/AdminForgotPasswordPage";
import AdminResetPasswordPage from "@/pages/AdminResetPasswordPage";
import AvailableTodayBrowsePage from "@/pages/AvailableTodayBrowsePage";
import AvailableTodayJoinPage from "@/pages/AvailableTodayJoinPage";
import AvailableTodayMyProfilePage from "@/pages/AvailableTodayMyProfilePage";
import AvailableTodayNewRequestPage from "@/pages/AvailableTodayNewRequestPage";
import AvailableTodayMyRequestsPage from "@/pages/AvailableTodayMyRequestsPage";
import AvailableTodayRequestDetailPage from "@/pages/AvailableTodayRequestDetailPage";
import LaborResponsePage from "@/pages/LaborResponsePage";
import AdminWorkforceSkillsPage from "@/pages/AdminWorkforceSkillsPage";
import FindAProPage from "@/pages/FindAProPage";
import AdminDirectoryPage from "@/pages/AdminDirectoryPage";
import AdminActivationsPage from "@/pages/AdminActivationsPage";
import AdminAnalyticsPage from "@/pages/AdminAnalyticsPage";
import AdminLaunchReadinessPage from "@/pages/AdminLaunchReadinessPage";
import AdminAdPreviewPage from "@/pages/AdminAdPreviewPage";
import LedgerPublicPage from "@/pages/LedgerPublicPage";
import SelfServeOnboardPage from "@/pages/SelfServeOnboardPage";
import AdminRecoverPage from "@/pages/AdminRecoverPage";
import ReactivationPage from "@/pages/ReactivationPage";

// Tiny helper to keep each admin route definition under one line of intent.
const Guard = (permission, Element) => (
  <ProtectedRoute permission={permission}><Element /></ProtectedRoute>
);

function App() {
  useEffect(() => {
    document.title = "NearNow · Market Your Business Automatically";
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <NavBar />
          <Routes>
            {/* Public / contractor-facing routes (NO auth) */}
            <Route path="/" element={<HomePage />} />
            <Route path="/onboard-simple" element={<OnboardSimple />} />
            <Route path="/onboard-tablet" element={<OnboardTablet />} />
            <Route path="/close" element={<OnboardTablet />} />
            <Route path="/sales-kit" element={<SalesKit />} />
            <Route path="/pitch" element={<SalesKit />} />
            <Route path="/contractor/:contractorId/grow" element={<ContractorGrow />} />
            <Route path="/grow" element={<ContractorGrow />} />
            <Route path="/contractor/:contractorId/referrals" element={<ContractorReferrals />} />
            <Route path="/refer" element={<ContractorReferrals />} />
            <Route path="/save/:token" element={<RetentionSavePage />} />
            <Route path="/r/:code" element={<ReferralRedirect />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/welcome" element={<WelcomePage />} />
            <Route path="/checkout/success" element={<WelcomePage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/why-nearnow" element={<WhyNearNow />} />
            <Route path="/jobs-today" element={<JobsToday />} />
            <Route path="/leads" element={<LeadsPage />} />
            <Route path="/testimonials" element={<TestimonialsPage />} />
            <Route path="/find-a-pro" element={<FindAProPage />} />
            <Route path="/directory" element={<FindAProPage />} />
            {/* Business Health Score (public + guided modes) */}
            <Route path="/assessment" element={<BusinessHealthAssessmentPage />} />
            <Route path="/health-score" element={<BusinessHealthAssessmentPage />} />
            <Route path="/assessment-report/:id" element={<AssessmentReportPage />} />

            {/* Available Today · Workforce Marketplace */}
            <Route path="/available-today" element={<AvailableTodayBrowsePage />} />
            <Route path="/available-today/join" element={<AvailableTodayJoinPage />} />
            <Route path="/available-today/me" element={<AvailableTodayMyProfilePage />} />
            <Route path="/available-today/requests" element={<AvailableTodayMyRequestsPage />} />
            <Route path="/available-today/requests/new" element={<AvailableTodayNewRequestPage />} />
            <Route path="/available-today/requests/:id" element={<AvailableTodayRequestDetailPage />} />
            <Route path="/labor/:token" element={<LaborResponsePage />} />

            {/* Auth pages */}
            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route path="/admin/auth-callback" element={<AdminAuthCallbackPage />} />
            <Route path="/admin/forgot-password" element={<AdminForgotPasswordPage />} />
            <Route path="/admin/reset-password" element={<AdminResetPasswordPage />} />
            <Route path="/admin/recover" element={<AdminRecoverPage />} />
            <Route path="/reactivate" element={<ReactivationPage />} />
            <Route path="/admin/me" element={<ProtectedRoute><MyRepStatsPage /></ProtectedRoute>} />
            <Route path="/admin/change-password" element={<ProtectedRoute><ChangePasswordPage /></ProtectedRoute>} />

            {/* Protected admin pages */}
            <Route path="/admin" element={Guard("view_admin_home", AdminDashboard)} />
            <Route path="/admin/users" element={
              <ProtectedRoute>
                {/* owner-only: gated server-side, but we redirect non-owners to /admin/me on the page itself via 403 */}
                <AdminUsersPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/rep-leaderboard" element={Guard("view_rep_leaderboard", AdminRepLeaderboard)} />
            <Route path="/admin/reps" element={Guard("view_rep_leaderboard", AdminRepLeaderboard)} />
            <Route path="/admin/ncge" element={Guard("view_ncge", AdminNCGE)} />
            <Route path="/admin/referrals" element={Guard("view_referrals", AdminReferralPayouts)} />
            <Route path="/admin/subscribers" element={Guard("view_subscribers", AdminSubscribers)} />
            <Route path="/admin/retention" element={Guard("view_retention", AdminRetentionPage)} />
            <Route path="/admin/saves" element={Guard("view_retention", AdminRetentionPage)} />
            <Route path="/admin/marketing" element={Guard("view_marketing", AdminMarketing)} />
            <Route path="/admin/inbox" element={Guard("view_inbox", AdminInbox)} />
            <Route path="/admin/discount-codes" element={Guard("view_discount_codes", AdminDiscountCodesPage)} />
            <Route path="/admin/codes" element={Guard("view_discount_codes", AdminDiscountCodesPage)} />
            <Route path="/admin/workforce-skills" element={Guard("view_admin_home", AdminWorkforceSkillsPage)} />
            <Route path="/admin/directory" element={Guard("view_admin_home", AdminDirectoryPage)} />
            <Route path="/admin/activations" element={Guard("view_admin_home", AdminActivationsPage)} />
            <Route path="/admin/analytics" element={Guard("view_admin_home", AdminAnalyticsPage)} />
            <Route path="/admin/launch-readiness" element={Guard("view_admin_home", AdminLaunchReadinessPage)} />
            <Route path="/admin/ad-preview" element={Guard("view_admin_home", AdminAdPreviewPage)} />
            <Route path="/ledger/:token" element={<LedgerPublicPage />} />
            <Route path="/onboard/self-serve" element={<SelfServeOnboardPage />} />
            <Route path="/admin/acos" element={Guard("view_admin_home", ComingSoonAcos)} />
            <Route path="/admin/seo" element={Guard("view_admin_home", ComingSoonSeo)} />

            <Route path="*" element={<NotFound />} />
          </Routes>
          <Footer />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

const ComingSoonAdminPanel = ({ name }) => (
  <div data-testid={`coming-soon-${name.replace(/\s+/g, "-")}`} style={{ padding: "60px 20px", maxWidth: 720, margin: "0 auto", textAlign: "center" }}>
    <span className="mock-badge">COMING SOON</span>
    <h1 className="font-display" style={{ fontSize: 48, color: "#0A1628", marginTop: 12 }}>{name}</h1>
    <p style={{ color: "#5a5d66", fontSize: 18 }}>
      This panel is scaffolded. Build slot is reserved in Phase 8 of the master plan.
    </p>
  </div>
);

const ComingSoonAcos = () => <ComingSoonAdminPanel name="ACOS A/B Testing Center" />;
const ComingSoonSeo = () => <ComingSoonAdminPanel name="SEO + AEO Content Hub" />;

const NotFound = () => (
  <div style={{ padding: 60, textAlign: "center" }}>
    <h1 className="font-display" style={{ fontSize: 56, color: "#0A1628" }}>404</h1>
    <p>Looks like that page doesn&apos;t exist.</p>
  </div>
);

export default App;
